import React, { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { defineMessages, useIntl } from 'react-intl';

// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { xcss } from '@atlaskit/primitives/xcss';
import Box from '@atlaskit/primitives/box';
import Text from '@atlaskit/primitives/text';
import Flex from '@atlaskit/primitives/components/flex';
import { token } from '@atlaskit/tokens';
import PageIcon from '@atlaskit/icon/core/page';

import type { PageMode } from '@confluence/page-utils/entry-points/enums.ts';
import {
	CommentType,
	type CommentData,
} from '@confluence/comments-data/entry-points/useCommentsData.ts';
import { OPTIMISTIC_SAVE_COMMENT_ID } from '@confluence/comments-data-manager/entry-points/commentDataManagerUtils.ts';
import { usePageInfo } from '@confluence/page-info/entry-points/usePageInfo.ts';
import type { FlagsStateContainer } from '@confluence/flags/entry-points/FlagsStateContainer.ts';
import type { CommentAction } from '@confluence/inline-comments-common/entry-points/inlineCommentsTypes.ts';
import { useAddCommentPermissionCheck } from '@confluence/comments-hooks/entry-points/useAddCommentPermissionCheck.ts';
import { commentsi18n } from '@confluence/comments-util/entry-points/commentsi18n.ts';
import {
	handleCommentsPanelNavigation,
	ScrollBehavior,
	NavigationContext,
} from '@confluence/comments-util/entry-points/commentsNavigationUtils.ts';
import { LocationValue } from '@confluence/comments-panel-utils/entry-points/commentsPanelHelper.ts';
import { useCommentsPanel } from '@confluence/comments-panel-utils/entry-points/useCommentsPanel.ts';
import { useShowCommentsPanel } from '@confluence/comments-panel/entry-points/useShowCommentsPanel.ts';
import {
	BaseCommentThread,
	type CommentComponentProps,
	type CommentRepliesComponentProps,
} from '@confluence/comments-common/entry-points/BaseCommentThread.ts';
import {
	type CommentThreadMouseEnterParams,
	handleCommentThreadMouseEnter,
	handleCommentThreadMouseLeave,
} from '@confluence/comments-common/entry-points/threadHelpers.ts';
import { expValEquals } from '@confluence/feature-experiments';
import { useAgentSessionsContext } from '@confluence/agent-mention/entry-points/AgentSessionsContext.ts';
import { useUnreadCommentsState } from '@confluence/unread-comments/entry-points/useUnreadComments.ts';
import { useIsEditorExpanded } from '@confluence/comments-common/entry-points/useIsEditorExpanded.ts';
import { getShouldShowRepliesSection } from '@confluence/comments-common/entry-points/repliesHelpers.ts';

import { ObjectComment } from './ObjectComment.tsx';
import { ObjectCommentReplies } from './ObjectCommentReplies.tsx';

export type ObjectCommentThreadProps = {
	parentComment: CommentData & {
		isFirstPageComment?: boolean;
	};
	supportedTopLevelActions: CommentAction[];
	pageMode: PageMode;
	flags?: FlagsStateContainer;
	shouldRenderCommentTypeSeparator?: boolean;
	isLastThread?: boolean;
	onLastThreadRendered?: () => void;
	isOffline?: boolean;
};

const commentThreadBoxStyles = xcss({
	listStyle: 'none',
	position: 'relative',
	paddingLeft: 'space.200',
	paddingRight: 'space.250',
	paddingTop: 'space.100', // On figma this is 200 but the object comment have 100px padding on top
	paddingBottom: 'space.200',
	margin: '0',
	display: 'flex',
	flexDirection: 'column',
	// Focus styles with outline - better containment
	':focus': {
		outlineOffset: 'space.negative.025',
	},
	':focus-visible': {
		outlineOffset: 'space.negative.025',
	},
});

const newCommentThreadBoxStyles = xcss({
	cursor: 'pointer',
});

const commentTypeSeperatorStyle = xcss({
	display: 'flex',
	gap: 'space.050',
	paddingTop: 'space.300',
	paddingRight: 'space.300',
	paddingBottom: 'space.150',
	paddingLeft: 'space.200',
	borderWidth: 'border.width',
	borderStyle: 'solid',
	borderColor: 'color.border',
});

const commentBodyBoxStyles = xcss({
	listStyle: 'none',
	position: 'relative',
	marginRight: 'space.100',
});

const isCurrentUserSelectedCommentStyles = xcss({
	background: token('color.background.input.hovered'),
});

const isThreadPressedStyles = xcss({
	background: token('color.background.input.hovered'),
});

const i18n = defineMessages({
	commentTypeSeperatorText: {
		id: 'object-comments.comment.thread.type-seperator',
		defaultMessage: 'General comments',
		description: 'Text for the seperator of inline and general comment section',
	},
	threadContentAriaLabelWithName: {
		id: 'object-comments.thread.content.aria-label.with-name',
		defaultMessage: '{name}’s comment thread',
		description:
			'Aria-label for content associated with the comment thread, with the first comment author name',
	},
});

const noop = () => {
	// This is intentional
};

// This component contains parent comment and its replies
export const ObjectCommentThread = ({
	parentComment,
	supportedTopLevelActions,
	pageMode,
	flags,
	shouldRenderCommentTypeSeparator,
	isLastThread,
	onLastThreadRendered,
	isOffline,
}: ObjectCommentThreadProps): React.JSX.Element => {
	const { formatMessage } = useIntl();

	const [
		{ currentlySelectedThreadKey, isCurrentlySelectedThreadOptimisticallyAdded },
		{ setCurrentlySelectedThreadKey },
	] = useCommentsPanel();
	const { pageInfo } = usePageInfo();
	const { unreadCommentIDSet } = useUnreadCommentsState();
	const { isCommentsPanelShown } = useShowCommentsPanel();

	const threadKey = parentComment.id;

	// The parent comment renders its agent-session status (AvatarWithActionButton) below
	// itself. Keep the thread's branching/connector line visible when a session exists so
	// the status doesn't visually float, even when the comment has no replies.
	const agentSessions = useAgentSessionsContext();
	const parentHasAgentSession =
		Boolean(agentSessions.get(parentComment.id)) &&
		expValEquals('cc_ai_agent_mention_from_comment', 'isEnabled', true);

	// State
	const [isThreadHovered, setIsThreadHovered] = useState<boolean>(false);
	const [isThreadPressed, setIsThreadPressed] = useState<boolean>(false);
	const [hoveredCommentId, setHoveredCommentId] = useState<string | undefined>(undefined);
	const [commentIsFocused, setCommentIsFocused] = useState<boolean>(false);

	const isEditorExpanded = useIsEditorExpanded(parentComment, LocationValue.FOOTER);

	const commentRef = useRef<HTMLDivElement>(null);

	// Hook dependent constants
	const isCurrentSelectedComment =
		currentlySelectedThreadKey === threadKey ||
		// Special handling to avoid UI flicker when optimistically adding a footer comment.
		// See comments in `next/packages/comments-panel-utils/src/useCommentsPanel.ts` for details.
		(parentComment.type === CommentType.GENERAL &&
			!!isCurrentlySelectedThreadOptimisticallyAdded &&
			threadKey === OPTIMISTIC_SAVE_COMMENT_ID);

	const { canAddComments } = useAddCommentPermissionCheck(pageInfo?.id ?? '', true);

	const handleCommentThreadHoverProps: CommentThreadMouseEnterParams = useMemo(
		() => ({
			threadKey,
			setIsThreadHovered,
			setHoveredCommentId,
			pageMode,
		}),
		[threadKey, setIsThreadHovered, setHoveredCommentId, pageMode],
	);

	const handleMouseEnter = useCallback(
		(id?: string) => {
			handleCommentThreadMouseEnter({
				...handleCommentThreadHoverProps,
				id,
			});
		},
		[handleCommentThreadHoverProps],
	);

	const handleMouseLeave = useCallback(() => {
		handleCommentThreadMouseLeave(handleCommentThreadHoverProps);
	}, [handleCommentThreadHoverProps]);

	const handleFocus = useCallback(
		(event: React.FocusEvent) => {
			event.stopPropagation();
			setCommentIsFocused(true);
		},
		[setCommentIsFocused],
	);

	const handleBlur = useCallback(
		(event: React.FocusEvent) => {
			// Only clear focus if we're not focusing a child element
			if (!event.relatedTarget || !event.currentTarget.contains(event.relatedTarget as Node)) {
				setCommentIsFocused(false);
			}
		},
		[setCommentIsFocused],
	);

	const handleCommentThreadMouseDown = () => {
		/**
		 * Limiting executions of this function because updating isThreadPressed
		 * rerenders every component in this tree. Most notably, a user clicking into
		 * the reply comment editor will call this function every time (and really any sibling component).
		 * 1. isThreadPressed is only used as a style variable alongside isCurrentSelectedComment
		 * 2. if clicking doesn't change threadKey then currentlySelectedThreadKey never changes either
		 * which are the two variables isCurrentSelectedComment compares. We should only call this function
		 * if threadKey has changed.
		 */
		if (!isCurrentSelectedComment) {
			if (!expValEquals('cc_comments_common_refactor', 'isEnabled', true)) {
				setIsThreadPressed(true);
			}
			setCurrentlySelectedThreadKey(threadKey);
		}
	};

	useEffect(() => {
		if (!expValEquals('cc_comments_common_refactor', 'isEnabled', true)) {
			const parentIdOfSelectedReply = parentComment?.replies?.find(
				(reply) => reply.id === currentlySelectedThreadKey,
			);
			if (isCurrentSelectedComment || parentIdOfSelectedReply) {
				setIsThreadPressed(true);
			} else {
				setIsThreadPressed(false);
			}
		}
	}, [currentlySelectedThreadKey, isCurrentSelectedComment, parentComment?.replies]);

	useEffect(() => {
		if (isLastThread && onLastThreadRendered) {
			// isLastThread should only be evaluated when it's a boolean value
			// Currently, its being incorrectly evaluated when passed as a function,
			// which is leading to unexpected behavior in the onLastThreadRendered callback
			if (typeof isLastThread === 'boolean') {
				onLastThreadRendered();
			}
		}
	}, [isLastThread, onLastThreadRendered]);

	const objectCommentThreadHandler = () => {
		setCurrentlySelectedThreadKey(threadKey);

		// Use unified navigation when comments panel is shown
		if (isCommentsPanelShown) {
			void handleCommentsPanelNavigation({
				annotationId: threadKey,
				commentType: 'FOOTER',
				scrollBehavior: ScrollBehavior.SMOOTH,
				block: 'center',
				context: NavigationContext.PANEL,
			});
		}
	};

	const commentComponent = (props: CommentComponentProps) => (
		<ObjectComment
			{...props}
			supportedTopLevelActions={supportedTopLevelActions}
			flags={flags}
			isOffline={isOffline}
		/>
	);

	const commentRepliesComponent = (props: CommentRepliesComponentProps) => (
		<ObjectCommentReplies {...props} isOffline={isOffline} flags={flags} />
	);

	return expValEquals('cc_comments_common_refactor', 'isEnabled', true) ? (
		<BaseCommentThread
			commentThreadRef={commentRef}
			commentComponent={commentComponent}
			commentRepliesComponent={commentRepliesComponent}
			parentComment={parentComment}
			pageMode={pageMode}
			shouldRenderCommentTypeSeparator={shouldRenderCommentTypeSeparator}
			canAddComments={canAddComments}
			handleCommentThreadClick={objectCommentThreadHandler}
			handleCommentThreadMouseDown={handleCommentThreadMouseDown}
			testIdPrefix={LocationValue.FOOTER}
			isEditorExpanded={isEditorExpanded}
			parentHasAgentSession={parentHasAgentSession}
		/>
	) : (
		<>
			{shouldRenderCommentTypeSeparator && (
				<Flex
					xcss={commentTypeSeperatorStyle}
					as="li"
					testId="comment-section-separator"
					alignItems="center"
				>
					<PageIcon
						testId="general-comment-page-icon"
						color={token('color.icon.subtlest')}
						label={formatMessage(i18n.commentTypeSeperatorText)}
					/>
					<Text color="color.text.subtlest" weight="medium">
						{formatMessage(i18n.commentTypeSeperatorText)}
					</Text>
				</Flex>
			)}
			{/* eslint-disable-next-line @atlassian/a11y/interactive-element-not-keyboard-focusable */}
			<Box
				ref={commentRef}
				id={`${LocationValue.FOOTER}-comment-thread-${threadKey}-container`}
				testId={`${LocationValue.FOOTER}-comment-thread-${threadKey}-container`}
				xcss={[
					commentThreadBoxStyles,
					isCurrentSelectedComment && isCurrentUserSelectedCommentStyles,
					!isCurrentSelectedComment && isThreadPressed && isThreadPressedStyles,
					newCommentThreadBoxStyles,
				]}
				onClick={objectCommentThreadHandler}
				onMouseDown={handleCommentThreadMouseDown}
				onMouseUp={() => setIsThreadPressed(false)}
				onFocus={
					expValEquals('confluence_frontend_fix_comment_actions_visibility', 'isEnabled', true)
						? undefined
						: handleFocus
				}
				onBlur={
					expValEquals('confluence_frontend_fix_comment_actions_visibility', 'isEnabled', true)
						? undefined
						: handleBlur
				}
				tabIndex={0}
				aria-label={formatMessage(i18n.threadContentAriaLabelWithName, {
					name: parentComment.author?.displayName || formatMessage(commentsi18n.anonymous),
				})}
			>
				<Box
					testId={`${LocationValue.FOOTER}-comment-thread-${parentComment.id}-parent`}
					xcss={commentBodyBoxStyles}
					onMouseEnter={
						expValEquals('confluence_frontend_fix_comment_actions_visibility', 'isEnabled', true)
							? undefined
							: () => handleMouseEnter(parentComment.id)
					}
					onMouseLeave={
						expValEquals('confluence_frontend_fix_comment_actions_visibility', 'isEnabled', true)
							? undefined
							: handleMouseLeave
					}
				>
					<ObjectComment
						comment={parentComment}
						supportedTopLevelActions={supportedTopLevelActions}
						pageInfo={pageInfo}
						pageMode={pageMode}
						isUnread={unreadCommentIDSet.has(parentComment.id)}
						isHovered={hoveredCommentId === parentComment.id || commentIsFocused}
						threadKey={threadKey}
						flags={flags}
						hideBranchingStyle={
							!parentHasAgentSession &&
							(expValEquals('confluence_comments_tuneup', 'isEnabled', true)
								? parentComment.replies.length === 0 && !isEditorExpanded
								: (!parentComment.isOpen || !canAddComments) && parentComment.replies.length === 0)
						}
						canAddComments={canAddComments}
						isThreadHovered={isThreadHovered}
						isOffline={isOffline}
					/>
				</Box>
				{getShouldShowRepliesSection({
					replies: parentComment.replies,
					isEditorExpanded,
				}) && (
					<ObjectCommentReplies
						parentComment={parentComment}
						pageInfo={pageInfo}
						hoveredCommentId={hoveredCommentId ?? ''}
						pageMode={pageMode}
						parentCommentId={parentComment.id}
						handleMouseEnter={
							expValEquals('confluence_frontend_fix_comment_actions_visibility', 'isEnabled', true)
								? noop
								: handleMouseEnter
						}
						handleMouseLeave={
							expValEquals('confluence_frontend_fix_comment_actions_visibility', 'isEnabled', true)
								? noop
								: handleMouseLeave
						}
						canAddComments={canAddComments}
						isOffline={isOffline}
						flags={flags}
						isEditorExpanded={isEditorExpanded}
					/>
				)}
			</Box>
		</>
	);
};
